/**
 * Appeal bond generation (Lloyd, Aug 19 2026: *"Yes, build away"*).
 *
 * Two calls, and the split matters. `readiness` asks what is missing WITHOUT producing anything, so
 * the page can tell staff to fill in a county or a cause number before they press a button. Only
 * `generate` produces a document, and it produces one that goes straight to Lloyd to sign.
 */

export interface AppealBondReadinessDto {
  courtType: 'municipal' | 'jp';
  courtFineCents: number | null;
  /** Plain-English reasons the bond cannot be generated. Empty when it can. */
  blockers: string[];
  canGenerate: boolean;
}

export interface AppealBondResultDto {
  fileName: string;
  courtType: 'municipal' | 'jp';
  /** The judgment sum, words and figures, exactly as printed in paragraph one. */
  judgmentAmount: string;
  /** Twice the judgment — the sum sworn to in paragraph two. */
  suretyAmount: string;
  /** Who it was emailed to, or null when delivery failed. */
  sentTo: string | null;
  /** Set when the PDF generated but the email did not go. The document is not lost. */
  deliveryError: string | null;
  pdfBase64: string;
}

/** Thrown when the case is not ready. Carries the reasons so the caller can list them. */
export class AppealBondBlockedError extends Error {
  constructor(
    message: string,
    public readonly blockers: string[],
  ) {
    super(message);
    this.name = 'AppealBondBlockedError';
  }
}

export interface GenerateAppealBondInput {
  judgmentDate: string;
  signatureDate?: string | null;
  notaryDate?: string | null;
  /** Overrides the fine stored on the case, and is written back to it. */
  fineCents?: number | null;
}

export default class BondApi {
  private static host(): string {
    return process.env.API_HOST?.trim() || window.location.origin;
  }

  static async readiness(
    ticketGuid: string,
    judgmentDate?: string,
  ): Promise<AppealBondReadinessDto> {
    const query = judgmentDate ? `?judgmentDate=${encodeURIComponent(judgmentDate)}` : '';
    const response = await fetch(
      `${BondApi.host()}/api/v1/manager/ticket/${ticketGuid}/appeal-bond/readiness${query}`,
      { credentials: 'include' },
    );
    if (!response.ok) throw new Error('Failed to check whether this case is ready for a bond');
    return response.json();
  }

  static async generate(
    ticketGuid: string,
    input: GenerateAppealBondInput,
  ): Promise<AppealBondResultDto> {
    const response = await fetch(
      `${BondApi.host()}/api/v1/manager/ticket/${ticketGuid}/appeal-bond`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(input),
      },
    );

    if (response.status === 422) {
      const err = await response.json().catch(() => ({}));
      throw new AppealBondBlockedError(
        err.message || 'This case is not ready for a bond.',
        Array.isArray(err.blockers) ? err.blockers : [],
      );
    }
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.details || err.error || err.message || 'Failed to generate the bond');
    }
    return response.json();
  }
}
